<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps({
  card: { type: Object, required: true },
});
const emit = defineEmits(['edit']);

const route = useRoute();
const router = useRouter();

// Adversarial fix: track dragging to suppress click events that fire after
// drag-and-drop in some browsers (especially when drag distance was tiny).
const isDragging = ref(false);
const dragThresholdMs = 200;
let dragStartTs = 0;

function onPointerDown() {
  dragStartTs = Date.now();
}
function onDragStart() {
  isDragging.value = true;
}
function onDragEnd() {
  // Defer reset so trailing click is suppressed.
  setTimeout(() => { isDragging.value = false; }, 100);
}

function suppressIfDragged() {
  return isDragging.value || Date.now() - dragStartTs > dragThresholdMs && false;
  // Note: returns true only if isDragging. The Date.now check is intentionally
  // ineffective here — kept for future tweaks. Drag suppression is the
  // primary guard.
}

function onCardClick() {
  if (suppressIfDragged()) return;
  emit('edit', props.card);
}

function onContactClick() {
  if (suppressIfDragged()) return;
  const accountId = route.params.accountId;
  if (props.card.primary_conversation_id) {
    router.push(`/app/accounts/${accountId}/conversations/${props.card.primary_conversation_id}`);
    return;
  }
  if (props.card.contact?.id) {
    router.push(`/app/accounts/${accountId}/contacts/${props.card.contact.id}`);
  }
}

const dueDateLabel = computed(() => {
  if (!props.card.due_at) return null;
  const date = new Date(props.card.due_at);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
});

const contactInitials = computed(() => {
  const name = props.card.contact?.name || '';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase() || '?';
});
</script>

<template>
  <article
    class="bg-n-solid-2 border border-n-weak rounded-md p-3 cursor-pointer hover:border-n-brand transition-colors"
    :class="{ 'border-red-500': card.overdue }"
    @pointerdown="onPointerDown"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="onCardClick"
  >
    <div class="flex items-start justify-between gap-2 mb-1.5">
      <h4 class="text-sm font-medium text-n-slate-12 line-clamp-2 flex-1">
        {{ card.title }}
      </h4>
      <span
        v-if="dueDateLabel"
        class="text-[10px] flex-shrink-0 px-1.5 py-0.5 rounded"
        :class="
          card.overdue
            ? 'bg-red-100 text-red-700'
            : 'bg-n-alpha-1 text-n-slate-11'
        "
      >
        {{ dueDateLabel }}
      </span>
    </div>

    <div class="flex items-center justify-between gap-2">
      <button
        type="button"
        class="flex items-center gap-1.5 min-w-0 hover:underline"
        title="Abrir conversa do contato"
        @click.stop="onContactClick"
      >
        <div
          class="size-5 rounded-full bg-n-brand/20 text-[10px] text-n-brand grid place-content-center flex-shrink-0 font-medium"
        >
          {{ contactInitials }}
        </div>
        <span class="text-xs text-n-slate-11 truncate">
          {{ card.contact?.name || 'Sem contato' }}
        </span>
      </button>

      <div
        v-if="card.labels?.length"
        class="flex items-center gap-1 flex-shrink-0"
      >
        <span
          v-for="label in card.labels.slice(0, 2)"
          :key="label"
          class="text-[10px] px-1.5 py-0.5 rounded bg-n-brand/10 text-n-brand"
        >
          {{ label }}
        </span>
        <span
          v-if="card.labels.length > 2"
          class="text-[10px] text-n-slate-10"
        >
          +{{ card.labels.length - 2 }}
        </span>
      </div>
    </div>
  </article>
</template>
